import { useEffect } from 'react';
import {
  type OpenAccountType,
  type OpenAccountFormValues,
  type TypeSelectItem,
  type AssetSelectItem,
  type LeverageSelectItem,
  useOpenAccountFormData,
  useOpenAccountForm,
  useOpenAccountRequest,
} from '../model';
import {
  ModalComponentWrapper,
  Button,
  Form,
  AssetBaseInfo,
  FormFieldCheckbox,
  FormFieldSelectInput,
  MarkdownText,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { useOpenAccountResult } from '../model/useOpenAccountResult';
import { OpenAccountFormTypeItem } from './OpenAccountFormTypeItem';
import type { OpenAccountResponseDto } from '../api/types';

type Props = {
  accountType: OpenAccountType;
  onSuccess: (
    data: OpenAccountResponseDto,
    accountType: OpenAccountType,
    assetCode?: string,
    accountCode?: string
  ) => void;
  onBack?: () => void;
};

const OpenAccountForm = ({ accountType, onSuccess, onBack }: Props) => {
  const { t } = useTranslation();
  const { goToSuccessScreen, goToErrorScreen } = useOpenAccountResult();

  const { formFields: initialFormFields } = useOpenAccountFormData(accountType);

  const { form } = useOpenAccountForm(initialFormFields);

  const selectedAccountCode = form.watch('tradingProductCode') as
    | string
    | undefined;

  const {
    activeCode,
    eulaText,
    formFields,
    typeSelectItems,
    assetSelectItems,
    leverageSelectItems,
  } = useOpenAccountFormData(accountType, selectedAccountCode);

  const {
    openAccount,
    isOpenAccountPending,
    isOpenAccountSuccess,
    isOpenAccountError,
    openAccountData,
    openAccountError,
  } = useOpenAccountRequest({
    accountType,
    onSuccess,
    assetCode: form.getValues('asset') as string,
    accountCode: selectedAccountCode || activeCode?.accountCode,
  });

  const onSubmit = (data: OpenAccountFormValues) => {
    delete data.eula;

    if ('leverageValue' in data) {
      data.leverageValue = Number(data.leverageValue);
    } else if (activeCode?.leverage?.length === 1) {
      data.leverageValue = Number(activeCode.leverage[0]);
    }

    if (!('tradingProductCode' in data) && activeCode) {
      data.tradingProductCode = activeCode.accountCode;
    }

    if (accountType === 'invest') {
      data.investType = data.tradingProductCode;
      delete data.tradingProductCode;
    }

    if (accountType === 'earn') {
      delete data.tradingProductCode;
    }

    if (accountType === 'spot') {
      delete data.tradingProductCode;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    openAccount(data as any);
  };

  useEffect(() => {
    if (isOpenAccountSuccess) {
      goToSuccessScreen(openAccountData);
    }
    if (isOpenAccountError) {
      goToErrorScreen();
    }
  }, [isOpenAccountSuccess, isOpenAccountError, openAccountError]);

  return (
    <ModalComponentWrapper
      title={t('openAccount.accountTitle', {
        accountType: t(
          `personalAccount.accountsSection.${accountType === 'margin' ? 'leverage' : accountType}Title`
        ),
      })}
      onBack={onBack}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-col gap-[1.6rem] w-full">
            {formFields.includes('tradingProductCode') && (
              <FormFieldSelectInput<TypeSelectItem>
                name="tradingProductCode"
                label={t('openAccount.typePlaceholder')}
                items={typeSelectItems}
                renderItem={item => <OpenAccountFormTypeItem item={item} />}
                renderSelectedValue={value => (
                  <OpenAccountFormTypeItem
                    item={typeSelectItems.find(item => item.value === value)}
                    showDescription={false}
                  />
                )}
              />
            )}

            {formFields.includes('asset') && (
              <FormFieldSelectInput<AssetSelectItem>
                name="asset"
                label={t('openAccount.assetPlaceholder')}
                items={assetSelectItems}
                renderItem={item => <AssetBaseInfo asset={item} />}
                renderSelectedValue={value => (
                  <AssetBaseInfo
                    asset={assetSelectItems.find(item => item.value === value)}
                    showName={false}
                    className="gap-[0.8rem]"
                    iconClassName="w-[1.8rem] h-[1.8rem]"
                  />
                )}
              />
            )}

            {formFields.includes('leverageValue') && (
              <FormFieldSelectInput<LeverageSelectItem>
                name="leverageValue"
                label={t('openAccount.leveragePlaceholder')}
                items={leverageSelectItems}
                renderItem={item => (
                  <div className="headline4">{item.title}</div>
                )}
                renderSelectedValue={value => (
                  <div className="headline4">
                    {leverageSelectItems
                      .find(item => item.value === value)
                      ?.title.replaceAll(' ', '')}
                  </div>
                )}
              />
            )}

            {formFields.includes('eula') && (
              <FormFieldCheckbox
                name="eula"
                label={
                  <MarkdownText
                    variant="eula"
                    isError={!!form.formState.errors.eula}>
                    {eulaText}
                  </MarkdownText>
                }
                hasError={!!form.formState.errors.eula}
                className="w-full py-[0.8rem]"
              />
            )}

            <Button
              type="submit"
              size="lg"
              loading={isOpenAccountPending}
              disabled={isOpenAccountPending}
              className="mt-[3.2rem]">
              {t('openAccount.submitButton')}
            </Button>
          </div>
        </form>
      </Form>
    </ModalComponentWrapper>
  );
};

export { OpenAccountForm };
